import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'

import { AIEmojisAPI } from '@makyoapp/makyo-sdk'
import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ActionFlag,
  BuilderDialog,
  IActionRepeaterValue,
  IActionType,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'
import { AlignCenter16, AlignLeft16, AlignRight16, Dialog16 } from '@makyoapp/makyo-icon/icon'
import { Button, Field, MaterialIconPicker, SingleIconSelect, Switch, Tabs } from '@makyoapp/makyo-ui'

import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useVariableTextSingleCell } from '@/hooks/__project__/useVariableTextSingleCell'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import { useVariableTextEdit } from '@/hooks/useVariableTextEdit'
import { BUILDER_DEVICES } from '@/shared/builder-constants'
import { ComponentPanelTab } from '@/shared/builder-ui'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'
import { BackButton } from '@/components/BackButton'
import { BrandKitColorsDropdown } from '@/components/BrandKitColorsDropdown'
import { DeviceTab } from '@/components/DeviceTab'
import { Help } from '@/components/Help'
import { TypographySelect } from '@/components/TypographySelect/TypographySelect'
import { VariableSimpleActionBody } from '@/components/VariableSelectV2/VariableSimpleAction'
import { VariableTextV2 } from '@/components/VariableTextV2/VariableTextV2'

import { SliderFieldSetting } from '../ComponentPanel/FieldSetting'

const iconAlignmentOptions = [
  {
    icon: <AlignLeft16 />,
    id: 'left',
    title: 'Start',
  },
  {
    icon: <AlignCenter16 />,
    id: 'center',
    title: 'Center',
  },
  {
    icon: <AlignRight16 />,
    id: 'right',
    title: 'End',
  },
]

interface DialogSettingBehaviorProps {
  dialog: BuilderDialog
  search: string
}

export const DialogSettingBehavior = ({ dialog: { id: dialogId }, search }: DialogSettingBehaviorProps) => {
  const { dialogs, updateDialog, setDialogAction, navbarSettings } = useAppBuilderContext()
  const [activeDevice, setActiveDevice] = useActiveDevice()

  const activeNavbar = navbarSettings[activeDevice]
  const device = activeNavbar.viewport as AppBreakpoint
  const devices = Object.keys(BUILDER_DEVICES) as AppBreakpoint[]

  const dialogIdx = useMemo(() => {
    return dialogs.findIndex((d) => d.id === dialogId)
  }, [dialogs, dialogId])

  const dialog = dialogs[dialogIdx]

  const [showPicker, setShowPicker] = useState(false)

  // handle dialog contents with variable text support
  const [varDialogName, setVarDialogName] = useVariableTextEdit({
    value: dialog.name,
    onChange: (value) => {
      updateDialog(dialogIdx, (prev) => {
        prev.name = value
      })
    },
  })

  const [varDialogHeadline, setVarDialogHeadline] = useVariableTextEdit({
    value: dialog.headline,
    onChange: (value) => {
      updateDialog(dialogIdx, (prev) => {
        prev.headline = value
      })
    },
  })

  const [varDialogContent, setVarDialogContent] = useVariableTextEdit({
    value: dialog.content,
    onChange: (value) => {
      updateDialog(dialogIdx, (prev) => {
        prev.content = value
      })
    },
  })

  const [varLeadingLabel, setVarLeadingLabel] = useVariableTextEdit({
    value: dialog.leadingLabel,
    onChange: (value) => {
      updateDialog(dialogIdx, (prev) => {
        prev.leadingLabel = value
      })
    },
  })

  const [varTrailingLabel, setVarTrailingLabel] = useVariableTextEdit({
    value: dialog.trailingLabel,
    onChange: (value) => {
      updateDialog(dialogIdx, (prev) => {
        prev.trailingLabel = value
      })
    },
  })

  // parsing dialog name
  const dialogName = useVariableTextSingleCell(dialog.name)

  // debounce for width changes
  const widthTimeoutRef = useRef<NodeJS.Timeout>()

  // Get default width based on device
  const getDefaultWidth = (device: AppBreakpoint): string => {
    const defaults = {
      phone: '325px',
      'phone-landscape': '400px',
      tablet: '450px',
      'tablet-landscape': '500px',
      desktop: '530px',
      tv: '610px',
    }
    return defaults[device]
  }

  const [tempWidth, setTempWidth] = useState<string>(dialog.width?.[device] || getDefaultWidth(device))

  const debouncedUpdateWidth = useCallback(
    (newWidth: string) => {
      if (widthTimeoutRef.current) {
        clearTimeout(widthTimeoutRef.current)
      }

      widthTimeoutRef.current = setTimeout(() => {
        updateDialog(dialogIdx, (prev) => {
          // Preserve existing values and only update the current device
          const updatedWidth = {
            ...prev.width,
            [device]: newWidth,
          }

          // Ensure all breakpoints have values by using existing values or sensible defaults
          const defaultWidths = {
            phone: '325px',
            'phone-landscape': '400px',
            tablet: '450px',
            'tablet-landscape': '500px',
            desktop: '530px',
            tv: '610px',
          }

          prev.width = {
            ...defaultWidths,
            ...updatedWidth,
          }
        })
      }, 300) // 300ms delay
    },
    [updateDialog, dialogIdx, device]
  )

  // cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (widthTimeoutRef.current) {
        clearTimeout(widthTimeoutRef.current)
      }
    }
  }, [])

  // update tempWidth when device changes
  useEffect(() => {
    setTempWidth(dialog.width?.[device] || getDefaultWidth(device))
  }, [device, dialog.width])

  const getFilteredAccordionItems = useCallback(() => {
    if (!search.trim()) {
      return {}
    }

    const searchLower = search.toLowerCase()

    return {}
  }, [search])

  const filteredItems = getFilteredAccordionItems()

  return (
    <Accordion openMultiple={false} initialActiveItems={['dialog-elements-visibility']}>
      <AccordionItem itemId='dialog-elements-visibility' title={<Trans>Elements Visibility</Trans>}>
        <Field>
          <label htmlFor='dialog-show-icon-switch'>
            <Trans>Show Icon</Trans>
          </label>
          <Switch
            id='dialog-show-icon-switch'
            checked={!!dialog.showIcon}
            onClick={async (isChecked) => {
              await updateDialog(dialogIdx, (prev) => {
                prev.showIcon = isChecked
              })
            }}
          />
        </Field>
        <Field>
          <label htmlFor='dialog-divider-switch'>
            <Trans>Show divider</Trans>
          </label>
          <Switch
            id='dialog-divider-switch'
            checked={dialog.showDivider}
            onClick={async (isChecked) => {
              await updateDialog(dialogIdx, (prev) => {
                prev.showDivider = isChecked
              })
            }}
          />
        </Field>
      </AccordionItem>

      <AccordionItem itemId='dialog-leading-actions' title={<Trans>Button Visibility</Trans>}>
        <Field>
          <label htmlFor='dialog-footer-leading-switch'>
            <Trans>Enable Leading Button</Trans>
          </label>
          <Switch
            id='dialog-footer-leading-switch'
            checked={dialog.showLeading}
            onClick={async (isChecked) => {
              await updateDialog(dialogIdx, (prev) => {
                prev.showLeading = isChecked
              })
            }}
          />
        </Field>
        <Field>
          <label htmlFor='dialog-footer-trailing-switch'>
            <Trans>Enable Trailing Button</Trans>
          </label>
          <Switch
            id='dialog-footer-trailing-switch'
            checked={dialog.showTrailing}
            onClick={async (isChecked) => {
              await updateDialog(dialogIdx, (prev) => {
                prev.showTrailing = isChecked
              })
            }}
          />
        </Field>
      </AccordionItem>
    </Accordion>
  )
}
